import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ArrowLeft, Lock } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { describeApiError } from '@/lib/permission-errors';
import { toast } from '@/stores/toast';
import { Modal } from '@/components/Modal';
import { NodeMultiSelect } from '@/components/NodeMultiSelect';
import { StoreMultiSelect } from '@/components/StoreMultiSelect';
import type { NodesResponse, NodeTreeItem } from '@/stores/scope';

/** StoreMultiSelectはcolorOfを要求するが、権限編集画面では店舗比較のような色分けの意味は無いため
 *  単一色を返す (Designレビュー HIGH反映: multi_storeスコープのUI欠如を修正するために追加)。 */
const NEUTRAL_STORE_COLOR = () => '#9CA3AF';

/**
 * v0.3 (S-16) ロール権限マトリクス編集画面。
 * 設計: docs/design/v03-custom-roles.md §4.3
 *
 * ⚠️ 実装乖離 (§8bへ後日転記予定): design doc のASCIIモックアップは「feature行1つに
 * action5列+共有scope1列」だったが、テスト計画S19 (同一feature内でaction毎にscopeが異なる
 * ロール) を素直に表現するには scope は permission_id (feature×action×scope) 単位で独立している
 * 必要がある。そのため実装では permission_id 単位のチェックボックス行 (feature毎にアコーディオン
 * グルーピング) に変更した。5列ToggleGroupの視覚的コンパクトさより、正確な状態表現を優先した。
 */

type Role = {
  role_id: string;
  role_name: string;
  description: string | null;
  is_system_role: boolean;
  is_system_reserved: boolean;
};

type CatalogPermission = {
  permission_id: string;
  feature: string;
  action: 'none' | 'view' | 'create' | 'update' | 'delete' | 'edit' | 'manage'
    | 'publish' | 'unpublish' | 'mark_not_applicable' | 'view_draft';
  // v0.4 章① (D-73): 'self' (本人のみ) を追加
  scope: 'platform' | 'all_stores' | 'multi_store' | 'own_store' | 'node_scope' | 'self';
};

type RolePermission = CatalogPermission & { store_ids: string[] | null; node_ids: string[] | null };

const featureLabels: Record<string, string> = {
  menu: 'メニューマスタ',
  ingredient: '食材マスタ',
  delivery_service: 'デリバリー業者マスタ',
  kpi_threshold: 'KPI閾値',
  manual_entry_sales: '手入力: 売上明細',
  manual_entry_delivery_details: '手入力: 納品明細',
  user: 'ユーザー管理',
  dashboard: 'ダッシュボード',
  import: 'データ取込 (CSV一括 / 人件費・諸経費・デリバリー売上の手入力)',
  export: 'データエクスポート',
  audit_logs: '監査ログ',
  role: 'ロール・権限',
  approval_workflow: '承認ワークフロー',
  config: 'コンフィグ',
  // v0.3 (S-1/S-10、中間REVIEW S-12 QA HIGH対応)
  publish: 'データ公開ゲート',
  // nav item単位の権限粒度化(論点B)によりai_menuを3分割 (v04-nav-item-permission-granularity-detail.md §2)。
  // ⚠️ design-expertレビュー指摘: 分割前は「AIメニュー開発」が3画面全体を指したため、分割後の
  // ai_menu_chat(チャット画面のみ)に同じラベルを残すと権限範囲を誤認させる(HIGH)。既存の
  // 「親: 子」連結規則(manual_entry_*系)に揃え、3行とも画面名を明示する(MEDIUM)。
  ai_menu_chat: 'AIメニュー開発: チャット',
  ai_menu_favorites: 'AIメニュー開発: お気に入り',
  ai_menu_constraints: 'AIメニュー開発: 制約事項',
  signature_profile: 'シグネチャ商品プロファイル',
  ai_ocr: 'AI-OCR (納品書取込)',
  // v0.3.x: AIトークン利用上限管理 (migration 0036) / AIコメント カスタム指示 (migration 0041)
  ai_usage: 'AI利用状況・利用上限',
  ai_instruction: 'AIコメント カスタム指示',
  // v0.4 章① (migration 0044): シフト管理・通知基盤の器 (実画面は章④以降)
  // nav item単位の権限粒度化(論点B、2026-09-10、worked example②): 旧shiftを10 featureへ分割
  shift_my: 'シフト: 自分のシフト',
  shift_periods: 'シフト: シフト期間',
  shift_records: 'シフト: 勤務実績',
  shift_monthly_entry: 'シフト: 月次実績一括入力',
  shift_urgent: 'シフト: 急な出勤要請',
  shift_print: 'シフト: シフト印刷',
  shift_report_daily: 'シフト: 日別サマリ',
  shift_report_employees: 'シフト: 従業員横断ビュー',
  shift_report_compliance: 'シフト: 36協定 遵守状況',
  shift_attendance_reason_master: 'シフト: 勤怠区分マスタ',
  shift_preference: 'シフト希望',
  staffing: '必要人員設定',
  // v0.4後半 月次実績一括入力 第2フェーズ I-1b (§3-2/§3-3、security-reviewer HIGH対応):
  // グレード・契約区分マスタ (employee-org-masters.ts) もこの feature を管理者権限として使う。
  // ラベルに明記しないと、custom role 設定者が「スキル・人材像・役職」のつもりでmanage権限を
  // 付与した際、労務条件マスタ (グレード・契約区分) まで意図せず管理可能になることに気づけない。
  skill: 'スキル・人材像・役職・グレード・契約区分',
  compensation: '給与単価',
  labor_cost_forecast: '人件費速報',
  income_cap: '年収の壁',
  agreement_36: '36協定',
  tenant_config: 'テナント設定 (ラベル・条件・階層継承)',
  notification: '通知設定',
  ai_setup: 'AIセットアップアシスタント',
  // v0.4 追加要件⑪ (migration 0107): シフトレポート (横断参照) の表示項目・計算式カタログ管理
  report_view: 'シフトレポート表示設定',
  // v0.4 追加要件⑭ (migration 0117): 売上目標。⚠️ ⑭ は V03_FEATURES にだけ追加してここを漏らしており、
  // ロール権限編集画面に英字の `kpi_target` が出ていた (§8-152、⑰ の恒久対策テストで検出)
  kpi_target: '売上目標',
  // v0.4 追加要件⑰ (migration 0118): 社内文書ライブラリ。
  // ⚠️⚠️ **これが permission の7番目の登録箇所** (§8-152 で6番目を記録した直後に platform-reviewer が発見)。
  //    参照は `featureLabels[feature] ?? feature` で**生の英字コードにフォールバックする**ため、
  //    登録を忘れても壊れず、日本語の中に `document` と出るだけになる (検出器が無い)。
  document: '社内文書ライブラリ',
  // v0.4 休暇管理 (TD-26、migration 0076/0092)。休暇管理のフロントエンド実装で
  // `/settings/leave-types` と `/leave/grants` の画面が生まれ、custom_roles=ON テナントが
  // これらをカスタムロールへ割り当てる必要が実際に生じたため V03_FEATURES と同時に追加した。
  leave: '休暇 (申請・残数)',
  leave_type: '休暇種別マスタ',
  leave_grant: '休暇の付与・管理簿',
  employee_family: '家族情報',
  // 使い勝手フィードバック⑥.5 (platform-reviewer Major指摘): TD-28 (migration 0090) で追加された
  // request_type 系4件が V03_FEATURES にだけ登録され、こちらへの反映が漏れていた (§8-152型)。
  request_type: '申請種別 (基本情報)',
  request_type_fields: '申請種別の入力項目',
  request_type_semantic: '申請種別の条件式・計算式',
  request_type_routing: '申請種別の対象者・承認接続',
  // 人・組織機能 増分① (migration 0138): 認定 (スキル・人材像の確定操作)。
  certification: '認定の確定',
  // 人・組織機能 増分③ (migration 0140): 配置制約 (E11/E12/E12b)。
  placement_constraint: '配置制約',
  // 人・組織機能 増分⑤ (migration 0141): 配置シミュレーション (SC-09)。
  placement_simulation: '配置シミュレーション',
  // 人・組織機能 増分⑦ (migration 0143): 育成計画 (E15/E16)。
  development_plan: '育成計画',
  // v0.5① 日報機能 増分1 (migration 0151): 日報。
  daily_report: '日報',
  daily_report_config: '日報の設定',
  // v0.5⑧ 経営会議PDCA (migration 0164): 経営会議・施策台帳。
  board_meeting: '経営会議',
  board_initiative: '施策台帳',
  // v0.5 AIメニュー開発の利用ランキング (migration 0172、§8-143): 未登録だと custom_roles=ON
  // テナントがこの feature をカスタムロールへ割り当てる手段が UI 上に存在しなくなる。
  ai_menu_usage_ranking: 'AIメニュー利用状況',
  // v0.4後半 月次実績一括入力 第2フェーズ I-6 出力機能 (migration 0189、§8-143): 未登録だと
  // custom_roles=ON テナントがこの feature をカスタムロールへ割り当てる手段が UI 上に存在しなくなる。
  attendance_export: '勤怠実績の出力',
};

const actionLabels: Record<string, string> = {
  view: '閲覧', create: '作成', update: '更新', delete: '削除', manage: '管理',
  publish: '公開', unpublish: '非公開化', mark_not_applicable: '対象外宣言', view_draft: '未公開データ閲覧',
};

const scopeLabels: Record<string, string> = {
  platform: '全テナント', all_stores: '全店舗', multi_store: '指定複数店舗', own_store: '自所属店舗のみ', node_scope: '指定ノード配下',
  // v0.4 章① (D-73/D-138): 「本人のみ」— ノード/店舗選択の対象外 (常に非表示、下の条件分岐が
  // node_scope/multi_store のみを見ているため self 行は自然にノード選択 UI が出ない)
  self: '本人のみ',
};

type SelectedMap = Record<string, { store_ids: string[] | null; node_ids: string[] | null }>;

export function RolePermissionsEditPage() {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: roles = [], isLoading: rolesLoading } = useQuery<Role[]>({ queryKey: ['roles'], queryFn: () => apiFetch<Role[]>('/roles') });
  const role = roles.find((r) => r.role_id === roleId);

  const { data: catalog = [] } = useQuery<CatalogPermission[]>({
    queryKey: ['permissions-catalog'],
    queryFn: () => apiFetch<CatalogPermission[]>('/roles/permissions/catalog'),
  });
  const { data: currentPermissions, isLoading } = useQuery<RolePermission[]>({
    queryKey: ['role-permissions', roleId],
    queryFn: () => apiFetch<RolePermission[]>(`/roles/${roleId}/permissions`),
    enabled: !!roleId,
  });
  const { data: nodesResponse } = useQuery<NodesResponse>({
    queryKey: ['nodes'],
    queryFn: () => apiFetch<NodesResponse>('/nodes'),
  });
  const nodeRoots: NodeTreeItem[] = nodesResponse?.roots ?? [];

  const [selected, setSelected] = useState<SelectedMap>({});
  const [initialized, setInitialized] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showDiff, setShowDiff] = useState(false);
  // v0.3 (S-16, design doc §0.1): Config系ページはデスクトップ(≥1024px)前提。
  // タブレット/モバイルでは閲覧のみとし編集操作をdisabledにする
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // currentPermissions到着時に一度だけ初期state構築 (以後の再fetchで編集内容を上書きしない)
  useEffect(() => {
    if (!currentPermissions || initialized) return;
    const map: SelectedMap = {};
    for (const p of currentPermissions) {
      map[p.permission_id] = { store_ids: p.store_ids, node_ids: p.node_ids };
    }
    setSelected(map);
    setInitialized(true);
  }, [currentPermissions, initialized]);

  const catalogByFeature = useMemo(() => {
    const groups = new Map<string, CatalogPermission[]>();
    for (const p of catalog) {
      if (!groups.has(p.feature)) groups.set(p.feature, []);
      groups.get(p.feature)!.push(p);
    }
    return groups;
  }, [catalog]);
  const featureOrder = Array.from(catalogByFeature.keys()).sort((a, b) =>
    (featureLabels[a] ?? a).localeCompare(featureLabels[b] ?? b, 'ja'));

  // QAレビュー MEDIUM反映: rolesクエリが未解決(直リンク/リロード直後の冷キャッシュ)の間、
  // role===undefinedを「編集可能」側にfallbackすると、system_reservedロールの権限ページを
  // 直接開いた際に一瞬チェックボックス/保存ボタンが有効に見えてしまう (バックエンドの
  // SYSTEM_ROLE_PROTECTEDで実害はないが誤操作を誘発する)。未解決中は保護されている扱いにする
  const isSystemReserved = rolesLoading || role === undefined ? true : role.is_system_reserved;

  const editDisabled = isSystemReserved || !isDesktop;

  const toggle = (p: CatalogPermission) => {
    if (editDisabled) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[p.permission_id]) {
        delete next[p.permission_id];
      } else {
        next[p.permission_id] = {
          store_ids: p.scope === 'multi_store' ? [] : null,
          node_ids: p.scope === 'node_scope' ? [] : null,
        };
      }
      return next;
    });
  };

  const setNodeIds = (permissionId: string, nodeIds: string[]) => {
    setSelected((prev) => ({ ...prev, [permissionId]: { ...prev[permissionId]!, node_ids: nodeIds } }));
  };

  const setStoreIds = (permissionId: string, storeIds: string[]) => {
    setSelected((prev) => ({ ...prev, [permissionId]: { ...prev[permissionId]!, store_ids: storeIds } }));
  };

  const diff = useMemo(() => {
    const beforeIds = new Set((currentPermissions ?? []).map((p) => p.permission_id));
    const afterIds = new Set(Object.keys(selected));
    const added = [...afterIds].filter((id) => !beforeIds.has(id));
    const removed = [...beforeIds].filter((id) => !afterIds.has(id));
    const catalogMap = new Map(catalog.map((p) => [p.permission_id, p]));
    const describe = (id: string) => {
      const p = catalogMap.get(id);
      if (!p) return id;
      return `${featureLabels[p.feature] ?? p.feature} / ${actionLabels[p.action] ?? p.action} (${scopeLabels[p.scope] ?? p.scope})`;
    };
    return { added: added.map(describe), removed: removed.map(describe) };
  }, [currentPermissions, selected, catalog]);

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/roles/${roleId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({
        permissions: Object.entries(selected).map(([permission_id, v]) => ({
          permission_id, store_ids: v.store_ids, node_ids: v.node_ids,
        })),
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['role-permissions', roleId] });
      setShowDiff(false);
      toast.success('権限を保存しました');
    },
    onError: (e) => {
      setShowDiff(false);
      toast.error(e instanceof ApiClientError ? describeApiError(e) : '保存に失敗しました');
    },
  });

  if (!roleId) return null;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/settings/roles" className="inline-flex items-center gap-1 text-sm text-neutral-sub hover:text-brand-red mb-2">
          <ArrowLeft size={14} /> ロール一覧に戻る
        </Link>
        <div className="flex items-center gap-2">
          <h2 className="text-h2">{role?.role_name ?? roleId} の権限編集</h2>
          {isSystemReserved && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-neutral-line text-neutral-text">
              <Lock size={11} /> システム標準ロール (閲覧のみ)
            </span>
          )}
        </div>
        {role?.description && <p className="text-sm text-neutral-sub mt-1">{role.description}</p>}
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="space-y-2">
          {featureOrder.map((feature) => {
            const perms = catalogByFeature.get(feature)!;
            const isOpen = expanded[feature] ?? false;
            const selectedCount = perms.filter((p) => selected[p.permission_id]).length;
            return (
              <div key={feature} className="border border-neutral-line rounded-lg bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpanded((prev) => ({ ...prev, [feature]: !isOpen }))}
                  aria-expanded={isOpen}
                  aria-controls={`feature-panel-${feature}`}
                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-neutral-panel text-left"
                >
                  <span className="font-medium text-sm">{featureLabels[feature] ?? feature}</span>
                  <span className="flex items-center gap-2 text-xs text-neutral-sub">
                    {selectedCount > 0 && <span className="tabular-nums text-brand-red font-semibold">{selectedCount}件選択中</span>}
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                </button>
                {isOpen && (
                  <div id={`feature-panel-${feature}`} className="border-t border-neutral-line divide-y divide-neutral-line" role="group" aria-label={`${featureLabels[feature] ?? feature}の権限`}>
                    {perms.map((p) => {
                      const isChecked = !!selected[p.permission_id];
                      return (
                        <div key={p.permission_id} className="flex items-center gap-3 px-4 py-2 text-sm">
                          <label className="flex items-center gap-2 flex-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={editDisabled}
                              onChange={() => toggle(p)}
                              className="w-4 h-4 accent-brand-red disabled:cursor-not-allowed"
                              aria-label={`${featureLabels[feature] ?? feature} - ${actionLabels[p.action] ?? p.action} (${scopeLabels[p.scope] ?? p.scope})`}
                              title={!isDesktop && !isSystemReserved ? '編集はデスクトップから' : undefined}
                            />
                            <span>{actionLabels[p.action] ?? p.action}</span>
                            <span className="text-xs text-neutral-sub">({scopeLabels[p.scope] ?? p.scope})</span>
                          </label>
                          {isChecked && p.scope === 'node_scope' && (
                            editDisabled ? (
                              <span className="text-xs text-neutral-sub tabular-nums">{selected[p.permission_id]?.node_ids?.length ?? 0}ノード選択済み</span>
                            ) : (
                              <NodeMultiSelect
                                nodes={nodeRoots}
                                selectedIds={new Set(selected[p.permission_id]?.node_ids ?? [])}
                                onChange={(next) => setNodeIds(p.permission_id, [...next])}
                              />
                            )
                          )}
                          {isChecked && p.scope === 'node_scope' && (selected[p.permission_id]?.node_ids?.length ?? 0) === 0 && (
                            <span className="text-[11px] text-warning-text">ノード未選択 (実質アクセス不可)</span>
                          )}
                          {isChecked && p.scope === 'multi_store' && (
                            editDisabled ? (
                              <span className="text-xs text-neutral-sub tabular-nums">{selected[p.permission_id]?.store_ids?.length ?? 0}店舗選択済み</span>
                            ) : (
                              <StoreMultiSelect
                                nodes={nodeRoots}
                                selectedIds={new Set(selected[p.permission_id]?.store_ids ?? [])}
                                onChange={(next) => setStoreIds(p.permission_id, [...next])}
                                colorOf={NEUTRAL_STORE_COLOR}
                                savedViewNamespace="role-permissions"
                              />
                            )
                          )}
                          {isChecked && p.scope === 'multi_store' && (selected[p.permission_id]?.store_ids?.length ?? 0) === 0 && (
                            <span className="text-[11px] text-warning-text">店舗未選択 (実質アクセス不可)</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isSystemReserved && (
        <div className="flex items-center justify-end gap-2 pt-2 sticky bottom-0 bg-surface-canvas py-3 border-t border-neutral-line">
          {!isDesktop && <span className="text-xs text-warning-text mr-auto">編集はデスクトップから行ってください (閲覧のみ)</span>}
          <button type="button" onClick={() => navigate('/settings/roles')}
            className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel text-sm">キャンセル</button>
          <button type="button" onClick={() => setShowDiff(true)} disabled={!initialized || !isDesktop}
            title={!isDesktop ? '編集はデスクトップから' : undefined}
            className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover text-sm disabled:opacity-50">
            保存
          </button>
        </div>
      )}

      <Modal
        open={showDiff}
        title="権限の変更内容を確認"
        onClose={() => setShowDiff(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowDiff(false)}
              className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel text-sm">キャンセル</button>
            <button type="button" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover text-sm disabled:opacity-50">
              {saveMut.isPending ? '保存中…' : '変更を適用'}
            </button>
          </div>
        )}
      >
        <div className="space-y-3 text-sm">
          {diff.added.length === 0 && diff.removed.length === 0 && (
            <p className="text-neutral-sub">変更はありません</p>
          )}
          {diff.added.length > 0 && (
            <div>
              <div className="font-medium text-brand-red mb-1">+ {diff.added.length} 件 追加</div>
              <ul className="list-disc pl-5 space-y-0.5 text-neutral-sub">
                {diff.added.map((d) => <li key={d}>{d}</li>)}
              </ul>
            </div>
          )}
          {diff.removed.length > 0 && (
            <div>
              <div className="font-medium text-warning-text mb-1">− {diff.removed.length} 件 削除</div>
              <ul className="list-disc pl-5 space-y-0.5 text-neutral-sub">
                {diff.removed.map((d) => <li key={d}>{d}</li>)}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
